import { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../config";
import s from "./ReportsPage.module.css";

/** Adds `.scrolling` while the user is actively scrolling; removes it ~700ms
 *  after the last event. Paired with CSS that paints the scrollbar thumb
 *  only when that class is present or the element is hovered. Same pattern
 *  used in SettingsPage's user table — duplicated here to keep this file
 *  self-contained without inventing a shared util. */
function useScrollActiveClass(activeClass) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let t;
    const onScroll = () => {
      el.classList.add(activeClass);
      clearTimeout(t);
      t = setTimeout(() => el.classList.remove(activeClass), 700);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(t); el.removeEventListener("scroll", onScroll); };
  }, [activeClass]);
  return ref;
}

/** Common categories known to the backend's ActivityLog writers. "" = all. */
const CATEGORIES = [
  { value: "",          label: "All categories" },
  { value: "AUTH",      label: "Authentication" },
  { value: "USER",      label: "Users" },
  { value: "STAFF",     label: "Staff" },
  { value: "STUDENT",   label: "Students" },
  { value: "PARENT",    label: "Parents" },
  { value: "ACADEMIC",  label: "Academics" },
  { value: "GRADE",     label: "Grades" },
  { value: "TUITION",   label: "Tuition" },
  { value: "BOOK",      label: "Books" },
  { value: "FACILITY",  label: "Facilities" },
  { value: "CONTENT",   label: "Site content" },
];

/** Format a LocalDateTime string into a friendly "Apr 22, 10:45 AM" style. */
function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function formatDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

/** Full activity log with filters. Backend returns newest-first already, so
 *  all the work here is filtering, grouping by day, and rendering. */
export default function ActivityTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const scrollRef = useScrollActiveClass(s.scrolling);
  const [sinceDays, setSinceDays] = useState("30"); // "7" | "30" | "90" | "all"

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    const url = category
      ? `${API_BASE}/api/activity/report?category=${encodeURIComponent(category)}`
      : `${API_BASE}/api/activity/report`;
    fetch(url)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`Load failed (${r.status})`)))
      .then(list => { if (!cancelled) setRows(Array.isArray(list) ? list : []); })
      .catch(e => !cancelled && setErr(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [category]);

  // Client-side search + date filter.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cutoff = sinceDays === "all"
      ? 0
      : Date.now() - Number(sinceDays) * 24 * 60 * 60 * 1000;
    return rows.filter(r => {
      if (cutoff && r.createdAt) {
        const t = new Date(r.createdAt).getTime();
        if (!Number.isNaN(t) && t < cutoff) return false;
      }
      if (!q) return true;
      return (r.username   || "").toLowerCase().includes(q)
          || (r.action     || "").toLowerCase().includes(q)
          || (r.targetName || "").toLowerCase().includes(q)
          || (r.details    || "").toLowerCase().includes(q)
          || (r.entityName || "").toLowerCase().includes(q);
    });
  }, [rows, search, sinceDays]);

  // Summary — how many events, distinct users, distinct categories.
  const summary = useMemo(() => {
    const users = new Set(filtered.map(r => r.username).filter(Boolean));
    const cats  = new Set(filtered.map(r => r.category).filter(Boolean));
    return { count: filtered.length, users: users.size, cats: cats.size };
  }, [filtered]);

  // Group by day for a scannable timeline.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const day = r.createdAt ? r.createdAt.slice(0, 10) : "unknown";
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(r);
    }
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div className={s.panel}>
      {/* Filter bar */}
      <div className={s.filterRow}>
        <label className={s.filterField}>
          <span>Category</span>
          <select value={category} onChange={e => setCategory(e.target.value)}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>
        <label className={s.filterField}>
          <span>Since</span>
          <select value={sinceDays} onChange={e => setSinceDays(e.target.value)}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </label>
        <label className={`${s.filterField} ${s.filterGrow}`}>
          <span>Search</span>
          <div className={s.searchWrap}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="User, target, action, details…"
            />
            {search && (
              <button className={s.clearBtn} onClick={() => setSearch("")} aria-label="Clear">✕</button>
            )}
          </div>
        </label>
      </div>

      {/* Summary pills */}
      <div className={s.summary}>
        <span className={s.summaryPill}>{summary.count} event{summary.count === 1 ? "" : "s"}</span>
        <span className={s.summaryPillMuted}>{summary.users} user{summary.users === 1 ? "" : "s"}</span>
        <span className={s.summaryPillMuted}>{summary.cats} categor{summary.cats === 1 ? "y" : "ies"}</span>
      </div>

      {err && <div className={s.error}>{err}</div>}

      {/* Timeline */}
      {loading ? (
        <div className={s.empty}>Loading…</div>
      ) : grouped.length === 0 ? (
        <div className={s.empty}>No events match the current filters.</div>
      ) : (
        <div className={s.timelineScrollWrap}>
        <div ref={scrollRef} className={s.timelineScroll}>
        <div className={s.timeline}>
          {grouped.map(([day, items]) => (
            <div key={day} className={s.timelineDay}>
              <div className={s.timelineHeader}>
                {day === "unknown" ? "Unknown date" : formatDay(items[0].createdAt)}
                <span className={s.timelineCount}>{items.length}</span>
              </div>
              <ul className={s.eventList}>
                {items.map(r => (
                  <li key={r.id} className={s.event}>
                    <div className={s.eventIcon}>{r.icon || "•"}</div>
                    <div className={s.eventBody}>
                      <div className={s.eventLine}>
                        <span className={s.eventUser}>{r.username || "system"}</span>
                        <span className={s.eventAction}>{r.action || r.actionType || "acted"}</span>
                        {r.targetName && <span className={s.eventTarget}>{r.targetName}</span>}
                      </div>
                      {r.details && <div className={s.eventDetails}>{r.details}</div>}
                    </div>
                    <div className={s.eventRight}>
                      {r.category && <span className={s.catPill}>{r.category}</span>}
                      <span className={s.eventTime}>{formatDate(r.createdAt)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        </div>
        </div>
      )}
    </div>
  );
}
